import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Upload,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import DonationService from "../../../services/DonationService";
import FinancialYearService from "../../../services/FinancialYearService";

const { Option } = Select;

const Donations = () => {
  const [list, setList] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await DonationService.getDonations();
      setList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load donations");
    } finally {
      setLoading(false);
    }
  };

  const fetchYears = async () => {
    try {
      const res = await FinancialYearService.getFinancialYears();
      setYears(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load financial years");
    }
  };

  useEffect(() => {
    fetch();
    fetchYears();
  }, []);

  const getYearName = useCallback(
    (id) => {
      const y = years.find((x) => x.id === id);
      const yearName = y ? y.name || y.year_name || id : id;
      return yearName ? `FY ${yearName}` : "-";
    },
    [years]
  );

  // Group data by financial year and sort by order within each group
  const groupedData = useMemo(() => {
    const groups = {};
    list.forEach((item) => {
      const fyId = item.financial_year;
      if (!groups[fyId]) {
        groups[fyId] = [];
      }
      groups[fyId].push(item);
    });
    // Sort items within each group by order
    Object.keys(groups).forEach((fyId) => {
      groups[fyId].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return Object.entries(groups).map(([fyId, items]) => ({
      key: `fy-${fyId}`,
      fyId: Number(fyId),
      fyName: getYearName(Number(fyId)),
      items,
      totalAmount: items.reduce((sum, i) => sum + (Number(i.amount) || 0), 0),
    }));
  }, [list, getYearName]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: "Marathi" });
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      donor_name: record.donor_name,
      amount: record.amount,
      order: record.order,
      description: record.description,
      language: record.language,
      financial_year: record.financial_year,
    });
    setSelectedFile(null);
    setPreviewUrl(record.image || null);
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedFile(null);
    if (previewUrl && previewUrl.startsWith && previewUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch (e) {}
    }
    setPreviewUrl(null);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete donation",
      content: "Are you sure?",
      async onOk() {
        try {
          await DonationService.deleteDonation(id);
          message.success("Deleted");
          fetch();
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const beforeUpload = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      message.error("Invalid image type. Allowed: jpg, png, webp");
      return false;
    }
    if (file.size > maxSize) {
      message.error("Image must be smaller than 5MB");
      return false;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return false;
  };

  const onFinish = async (values) => {
    const payload = {
      donor_name: values.donor_name || "",
      amount_label: "",
      amount: values.amount || null,
      order: values.order != null ? values.order : 0,
      description: values.description || "",
      language: values.language || "",
      financial_year: values.financial_year || null,
    };

    setLoading(true);
    try {
      if (editing) {
        await DonationService.updateDonation(editing.id, payload, selectedFile);
        message.success("Updated");
      } else {
        await DonationService.createDonation(payload, selectedFile);
        message.success("Created");
      }
      setIsModalOpen(false);
      form.resetFields();
      setSelectedFile(null);
      if (
        previewUrl &&
        previewUrl.startsWith &&
        previewUrl.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (e) {}
      }
      setPreviewUrl(null);
      fetch();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 80,
      render: (img) =>
        img ? (
          <img
            src={img}
            alt="donation"
            style={{
              width: 50,
              height: 50,
              objectFit: "cover",
              borderRadius: 4,
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Donor Name",
      dataIndex: "donor_name",
      key: "donor_name",
      render: (t) => (
        <div style={{ maxWidth: 220, whiteSpace: "normal" }}>{t}</div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => (v ? `₹${Number(v).toLocaleString("en-IN")}` : "-"),
    },
    { title: "Order", dataIndex: "order", key: "order" },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (d) => (
        <div style={{ maxWidth: 300, whiteSpace: "normal" }}>{d}</div>
      ),
    },
    { title: "Language", dataIndex: "language", key: "language" },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(r)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(r.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>Donations</h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add
          </Button>
        </div>
      </div>

      {groupedData.length === 0 ? (
        <Table columns={columns} dataSource={[]} loading={loading} />
      ) : (
        groupedData.map((group) => (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <div
              style={{
                background: "linear-gradient(90deg, #001529 0%, #042035 100%)",
                padding: "12px 16px",
                borderRadius: "8px 8px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: "white", fontWeight: 600, fontSize: 16 }}>
                {group.fyName}
              </span>
              <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
                Total: ₹{group.totalAmount.toLocaleString("en-IN")}
              </span>
            </div>
            <Table
              columns={columns}
              dataSource={group.items}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              style={{ borderRadius: "0 0 8px 8px", overflow: "hidden" }}
            />
          </div>
        ))
      )}

      <Modal
        title={editing ? "Edit Donation" : "Create Donation"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ language: "Marathi" }}
        >
          <Form.Item
            name="donor_name"
            label="Donor Name"
            rules={[{ required: true, message: "Please enter donor name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="amount" label="Amount">
            <InputNumber style={{ width: "100%" }} precision={2} />
          </Form.Item>

          <Form.Item
            name="financial_year"
            label="Financial Year"
            rules={[
              { required: true, message: "Please select financial year" },
            ]}
          >
            <Select
              showSearch
              placeholder="Select Financial Year"
              options={years.map((y) => ({
                label: `FY ${y.name || y.year_name}`,
                value: y.id,
              }))}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item name="order" label="Order">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Display order"
            />
          </Form.Item>

          <Form.Item label="Image">
            <Upload
              accept="image/*"
              beforeUpload={beforeUpload}
              onRemove={() => {
                setSelectedFile(null);
                if (
                  previewUrl &&
                  previewUrl.startsWith &&
                  previewUrl.startsWith("blob:")
                ) {
                  try {
                    URL.revokeObjectURL(previewUrl);
                  } catch (e) {}
                }
                setPreviewUrl(editing?.image || null);
              }}
              fileList={selectedFile ? [selectedFile] : []}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Choose Image</Button>
            </Upload>
            {previewUrl && (
              <img
                src={previewUrl}
                alt="preview"
                style={{
                  marginTop: 8,
                  maxWidth: "100%",
                  maxHeight: 150,
                  objectFit: "contain",
                  borderRadius: 4,
                }}
              />
            )}
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item name="language" label="Language">
            <Select placeholder="Select Language">
              <Option value="English">English</Option>
              <Option value="Marathi">Marathi</Option>
              <Option value="Hindi">Hindi</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Donation Details"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {viewing && (
          <div>
            {viewing.image && (
              <div style={{ marginBottom: 16, textAlign: "center" }}>
                <img
                  src={viewing.image}
                  alt="donation"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    objectFit: "contain",
                    borderRadius: 8,
                  }}
                />
              </div>
            )}
            <p>
              <strong>Donor Name:</strong> {viewing.donor_name}
            </p>
            <p>
              <strong>Amount:</strong>{" "}
              {viewing.amount
                ? `₹${Number(viewing.amount).toLocaleString("en-IN")}`
                : "-"}
            </p>
            <p>
              <strong>Financial Year:</strong>{" "}
              {getYearName(viewing.financial_year)}
            </p>
            <p>
              <strong>Order:</strong> {viewing.order}
            </p>
            <p>
              <strong>Description:</strong>
              <br />
              {viewing.description}
            </p>
            <p>
              <strong>Language:</strong> {viewing.language}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Donations;
